import {
  IDateRange,
  DateRangePreset,
  IAlertSeverityData,
  IAlertSourceData,
  IIntegrationAdoption,
  IAnalyticsErrorMessagesParams,
  IAlertStatsSourceItem,
  IAlertStatsSeverityItem,
  IOrganizationAlertStatisticsApiItem,
  IIntegrationAnalyticsApiItem,
  IIntegrationsAnalyticsApiResponse,
  IUsersAnalyticsResponse,
  IOrganizationsAnalyticsResponse,
  IVersionDistributionResponse,
  IDeviceAnalyticsData,
  IOrganizationBaseDetailsApiItem,
  IDeviceGrowthApiItem,
  IAlertsAnalyticsApiResponse,
  IOrganizationAlertStatisticsItem,
  IGlobalAlertStatistics,
} from './analytics.types';

export const getDateRangeLabel = (preset: DateRangePreset): string => {
  switch (preset) {
    case '7d':
      return 'Last 7 days';
    case '30d':
      return 'Last 30 days';
    case '90d':
      return 'Last 90 days';
    case '6m':
      return 'Last 6 months';
    case '1y':
      return 'Last year';
    case 'custom':
      return 'Custom Range';
    default:
      return 'Last 30 days';
  }
};

export const getDefaultAnalyticsDateRange = (): IDateRange => {
  const currentDate = new Date();
  const rangeStartDate = new Date();
  rangeStartDate.setDate(rangeStartDate.getDate() - 29);

  return {
    preset: '30d',
    startDate: rangeStartDate.toISOString().split('T')[0],
    endDate: currentDate.toISOString().split('T')[0],
    label: 'Last 30 days',
  };
};

export const computeDateRangeFromPreset = (
  preset: DateRangePreset
): { startDate: string; endDate: string } => {
  const currentDate = new Date();
  const rangeStartDate = new Date();

  switch (preset) {
    case '7d':
      rangeStartDate.setDate(rangeStartDate.getDate() - 6);
      break;
    case '30d':
      rangeStartDate.setDate(rangeStartDate.getDate() - 29);
      break;
    case '90d':
      rangeStartDate.setDate(rangeStartDate.getDate() - 89);
      break;
    case '6m':
      rangeStartDate.setMonth(rangeStartDate.getMonth() - 6);
      break;
    case '1y':
      rangeStartDate.setFullYear(rangeStartDate.getFullYear() - 1);
      break;
    default:
      rangeStartDate.setDate(rangeStartDate.getDate() - 29);
      break;
  }

  return {
    startDate: rangeStartDate.toISOString().split('T')[0],
    endDate: currentDate.toISOString().split('T')[0],
  };
};

export const buildStandardRangeQuery = (dateRange: IDateRange): string => {
  const queryParameters = new URLSearchParams();

  queryParameters.set('range', dateRange.preset);

  if (dateRange.preset === 'custom') {
    queryParameters.set('startDate', dateRange.startDate);
    queryParameters.set('endDate', dateRange.endDate);
  }

  return queryParameters.toString();
};

export const buildAlertsRangeQuery = (dateRange: IDateRange): string => {
  const queryParameters = new URLSearchParams();

  queryParameters.set('range', dateRange.preset);

  if (dateRange.preset === 'custom') {
    queryParameters.set('startDateTime', `${dateRange.startDate}T00:00:00.000Z`);
    queryParameters.set('endDateTime', `${dateRange.endDate}T23:59:59.999Z`);
  }

  return queryParameters.toString();
};

const severityColorMap: Record<string, string> = {
  high: 'var(--fuji-severity-high)',
  medium: 'var(--fuji-severity-medium)',
  low: 'var(--fuji-severity-low)',
};

export const formatSeverityLabel = (severityValue: string): string =>
  severityValue.charAt(0).toUpperCase() + severityValue.slice(1);

export const getSeverityColor = (severityValue: string): string =>
  severityColorMap[String(severityValue || '').toLowerCase()] ||
  'var(--fuji-color-text-secondary)';

export const normalizeOrganizationsAnalyticsResponse = (
  responseData: IOrganizationsAnalyticsResponse
) => {
  const organizations = Array.isArray(responseData?.organizations)
    ? responseData.organizations
    : [];

  return {
    totalOrgCount: Number(responseData?.totalOrgCount || 0),
    invitedOrgCount: Number(responseData?.invitedOrgCount || 0),
    disabledOrgCount: Number(responseData?.disabledOrgCount || 0),
    activeOrgCount: Number(responseData?.activeOrgCount || 0),
    organizations: organizations.map(
      (organizationRecord: IOrganizationsAnalyticsResponse['organizations'][number]) => ({
        orgId: String(organizationRecord?.orgID || organizationRecord?.orgId || ''),
        orgName: String(organizationRecord?.orgName || '').trim(),
      })
    ),
  };
};

export const normalizeUsersAnalyticsResponse = (
  responseData: IUsersAnalyticsResponse
) => {
  const organizationWiseUsers = Array.isArray(responseData?.organizationWiseUsers)
    ? responseData.organizationWiseUsers
    : [];

  return {
    totalUsersCount: Number(responseData?.totalUsersCount || 0),
    organizationWiseUsers: organizationWiseUsers.map(
      (organizationUser: IUsersAnalyticsResponse['organizationWiseUsers'][number]) => ({
        orgId: String(organizationUser?.orgId || ''),
        users: Number(organizationUser?.users || 0),
      })
    ),
  };
};

export const normalizeDeviceAnalyticsResponse = (
  responseData: IDeviceAnalyticsData
) => {
  const organizationBaseDetails = Array.isArray(responseData?.organizationBaseDetails)
    ? responseData.organizationBaseDetails
    : [];

  const organizationWiseDevices = organizationBaseDetails.map(
    (organizationDevice: IOrganizationBaseDetailsApiItem) => ({
      orgId: String(organizationDevice?.organizationId || ''),
      orgName: String(organizationDevice?.organizationName || '').trim(),
      totalDevices: Number(organizationDevice?.totalDevices || 0),
      onlineDevices: Number(organizationDevice?.onlineDevices || 0),
      devicesAdded: Number(organizationDevice?.devicesAddedInSelectedRange || 0),
      deviceGrowth: Array.isArray(organizationDevice?.deviceGrowth)
        ? organizationDevice.deviceGrowth.map((growthPoint: IDeviceGrowthApiItem) => ({
            label: String(growthPoint?.label || ''),
            count: Number(growthPoint?.count || 0),
          }))
        : [],
      deviceGrowthDateGroupType: String(
        organizationDevice?.deviceGrowthDateGroupType || ''
      ),
    })
  );

  return {
    totalDevices: Number(responseData?.totalDeviceCount?.totalDevices || 0),
    onlineDevices: Number(responseData?.totalDeviceCount?.onlineDevices || 0),
    offlineDevices: Number(responseData?.totalDeviceCount?.offlineDevices || 0),
    organizationWiseDevices,
  };
};

const updateStatusColorMap: Record<string, string> = {
  success: 'var(--fuji-severity-low)',
  pending: 'var(--fuji-severity-medium)',
  failed: 'var(--fuji-severity-high)',
  mismatched: 'var(--fuji-status-mismatched)',
};

export const mapVersionStatusObjectToArray = (
  statusObject: Record<string, { count: number; percentage: number }> | undefined
): { status: string; count: number; percentage: number; color: string }[] => {
  if (!statusObject || typeof statusObject !== 'object') {
    return [];
  }

  return Object.entries(statusObject).map(([statusKey, statusValue]: [string, any]) => ({
    status: statusKey.charAt(0).toUpperCase() + statusKey.slice(1),
    count: Number(statusValue?.count || 0),
    percentage: Number(statusValue?.percentage || 0),
    color: updateStatusColorMap[statusKey] || 'var(--fuji-color-text-secondary)',
  }));
};

export const normalizeVersionDistributionResponse = (
  responseData: IVersionDistributionResponse
) => {
  const firmwareVersions = Array.isArray(responseData?.firmwareVersionDistribution)
    ? responseData.firmwareVersionDistribution.map((firmwareVersion) => ({
        version: String(firmwareVersion?.version || ''),
        deviceCount: Number(firmwareVersion?.deviceCount || 0),
        percentage: Number(firmwareVersion?.percentage || 0),
      }))
    : [];

  const iotAgentVersions = Array.isArray(responseData?.iotAgentVersionDistribution)
    ? responseData.iotAgentVersionDistribution.map((iotAgentVersion) => ({
        version: String(iotAgentVersion?.version || ''),
        deviceCount: Number(iotAgentVersion?.deviceCount || 0),
        percentage: Number(iotAgentVersion?.percentage || 0),
      }))
    : [];

  return {
    firmwareVersions,
    iotAgentVersions,
    firmwareUpdateStatus: mapVersionStatusObjectToArray(
      responseData?.firmwareUpdateStatus
    ),
    iotAgentUpdateStatus: mapVersionStatusObjectToArray(
      responseData?.iotAgentUpdateStatus
    ),
  };
};
export const normalizeAlertsAnalyticsResponse = (
  responseData: IAlertsAnalyticsApiResponse
) => {
  const activeAlertStats = responseData?.activeAlertStats || {};
  const severityObject = activeAlertStats?.severity || {};
  const sourceObject = activeAlertStats?.source || {};

  const alertsBySeverity: IAlertSeverityData[] = Object.entries(severityObject).map(
    ([severityKey, severityCount]) => ({
      severity: formatSeverityLabel(severityKey),
      count: Number(severityCount || 0),
      color: getSeverityColor(severityKey),
    })
  );

  const alertsBySource: IAlertSourceData[] = Object.entries(sourceObject).map(
    ([sourceKey, sourceCount]) => ({
      source: String(sourceKey),
      count: Number(sourceCount || 0),
    })
  );

  const orgStatistics: IOrganizationAlertStatisticsItem[] = Array.isArray(responseData?.orgStats)
    ? responseData.orgStats.map((organizationAlertStat: IOrganizationAlertStatisticsApiItem) => ({
        orgId: String(organizationAlertStat?.orgId || ''),
        orgName: String(organizationAlertStat?.orgName || '').trim(),
        totalAlerts: Number(organizationAlertStat?.totalAlerts || 0),
        successCount: Number(organizationAlertStat?.successCount || 0),
        failedCount: Number(organizationAlertStat?.failedCount || 0),
        topSource: String(organizationAlertStat?.topSource || ''),
        topSourceCount: Number(organizationAlertStat?.topSourceCount || 0),
        successRate: Number(organizationAlertStat?.successRate || 0),
        failedRate: Number(organizationAlertStat?.failedRate || 0),
        sourceStats: Array.isArray(organizationAlertStat?.sourceStats)
          ? organizationAlertStat.sourceStats.map((sourceStat: IAlertStatsSourceItem) => ({
              source: String(sourceStat?.source || ''),
              count: Number(sourceStat?.count || 0),
            }))
          : [],
        severityStats: Array.isArray(organizationAlertStat?.severityStats)
          ? organizationAlertStat.severityStats.map((severityStat: IAlertStatsSeverityItem) => ({
              severity: String(severityStat?.severity || ''),
              count: Number(severityStat?.count || 0),
            }))
          : [],
      }))
    : [];

  const rawGlobalAlertStats = responseData?.globalAlertStats;

  const globalAlertStats: IGlobalAlertStatistics = rawGlobalAlertStats
    ? {
        totalAlerts: Number(rawGlobalAlertStats?.totalAlerts || 0),
        successCount: Number(rawGlobalAlertStats?.successCount || 0),
        failedCount: Number(rawGlobalAlertStats?.failedCount || 0),
        avgSuccessRate: Number(rawGlobalAlertStats?.avgSuccessRate || 0),
        failedRate: Number(rawGlobalAlertStats?.failedRate || 0),
        sourceStats: Array.isArray(rawGlobalAlertStats?.sourceStats)
          ? rawGlobalAlertStats.sourceStats.map((sourceStat: IAlertStatsSourceItem) => ({
              source: String(sourceStat?.source || ''),
              count: Number(sourceStat?.count || 0),
            }))
          : [],
        severityStats: Array.isArray(rawGlobalAlertStats?.severityStats)
          ? rawGlobalAlertStats.severityStats.map((severityStat: IAlertStatsSeverityItem) => ({
              severity: String(severityStat?.severity || ''),
              count: Number(severityStat?.count || 0),
            }))
          : [],
      }
    : {
        totalAlerts: 0,
        successCount: 0,
        failedCount: 0,
        avgSuccessRate: 0,
        failedRate: 0,
        sourceStats: [],
        severityStats: [],
      };

  return {
    totalAlertsCount: Number(globalAlertStats.totalAlerts || 0),
    totalActiveAlerts: Number(activeAlertStats?.totalActiveAlerts || 0),
    alertsBySeverity,
    alertsBySource,
    orgStatistics,
    globalAlertStats,
  };
};

export const normalizeIntegrationsAnalyticsResponse = (
  responseData: IIntegrationsAnalyticsApiResponse
): IIntegrationAdoption[] => {
  const integrations: IIntegrationAnalyticsApiItem[] = Array.isArray(responseData.integrations)
    ? responseData.integrations
    : [];

  const totalOrganizations = Number(responseData.totalOrganizations || 0);

  return integrations.map(
    (integrationRecord: IIntegrationAnalyticsApiItem): IIntegrationAdoption => ({
      name: String(integrationRecord.integrationName || 'Unknown'),
      orgsUsing: Number(integrationRecord.totalAdoptedOrgCount || 0),
      totalOrgs: totalOrganizations,
      percentage: Number(integrationRecord.percentage || 0),
      organizations: Array.isArray(integrationRecord.organizations)
        ? integrationRecord.organizations.map((organization) => ({
            orgId: String(organization?.orgId || ''),
            orgName: String(organization?.orgName || organization?.orgId || ''),
            adminEmail: String(organization?.adminEmail || ''),
            ...(typeof organization?.webhookUrlUpdated === 'boolean'
              ? { webhookUrlUpdated: organization.webhookUrlUpdated }
              : {}),
          }))
        : [],
      ...(integrationRecord.webhookMigration
        ? {
            webhookMigration: {
              updatedCount: Number(
                integrationRecord.webhookMigration.updatedCount || 0,
              ),
              pendingCount: Number(
                integrationRecord.webhookMigration.pendingCount || 0,
              ),
            },
          }
        : {}),
    })
  );
};

export const getFailedAnalyticsMessage = ({
  organizationsError,
  usersError,
  deviceError,
  alertsError,
  integrationsError,
  versionDistributionError,
}: IAnalyticsErrorMessagesParams): string | null => {
  if (organizationsError) {
    return 'Organization analytics data could not be loaded at the moment. Please try again.';
  }

  if (usersError) {
    return 'User analytics data could not be loaded at the moment. Please try again.';
  }

  if (deviceError) {
    return 'Device analytics data could not be loaded at the moment. Please try again.';
  }

  if (alertsError) {
    return 'Alerts analytics data could not be loaded at the moment. Please try again.';
  }

  if (integrationsError) {
    return 'Integration analytics data could not be loaded at the moment. Please try again.';
  }

  if (versionDistributionError) {
    return 'Version distribution data could not be loaded at the moment. Please try again.';
  }

  return null;
};